// eslint-disable-next-line @github-ui/github-monorepo/prefer-github-ui-react-router
import {
  createPath,
  createSearchParams,
  matchRoutes,
  resolvePath,
  useLocation,
  type NavigateOptions,
  type To,
  type UNSAFE_createBrowserHistory,
  type URLSearchParamsInit,
  useNavigate as useReactRouterNavigate,
  useSearchParams as useReactRouterSearchParams,
  type RouteObject,
} from 'react-router'
import isHashNavigation from '@github-ui/is-hash-navigation'
import {startSoftNav} from '@github-ui/soft-nav/state'
import {createContext, useCallback, use} from 'react'

// eslint-disable-next-line @github-ui/github-monorepo/prefer-github-ui-react-router
export type {
  Blocker,
  ErrorResponse,
  LinkProps,
  LoaderFunction,
  Location,
  GetScrollRestorationKeyFunction,
  HistoryRouterProps,
  InitialEntry,
  MemoryRouterProps,
  Navigator,
  NavigateOptions,
  NavigateProps,
  NavLinkProps,
  Params,
  PathParam,
  RelativeRoutingType,
  RouteObject,
  RouterProviderProps,
  SetURLSearchParams,
  ShouldRevalidateFunction,
  ShouldRevalidateFunctionArgs,
  To,
  UIMatch,
  URLSearchParamsInit,
} from 'react-router'

// eslint-disable-next-line @github-ui/github-monorepo/prefer-github-ui-react-router
export {
  BrowserRouter,
  createBrowserRouter,
  createMemoryRouter,
  createPath,
  createSearchParams,
  createStaticHandler,
  createStaticRouter,
  generatePath,
  isRouteErrorResponse,
  Link,
  matchPath,
  matchRoutes,
  MemoryRouter,
  NavigationType,
  NavLink,
  Navigate,
  Outlet,
  resolvePath,
  Route,
  Router,
  RouterProvider,
  Routes,
  ScrollRestoration,
  StaticRouter,
  StaticRouterProvider,
  UNSAFE_createBrowserHistory,
  UNSAFE_NavigationContext,
  unstable_usePrompt,
  useBeforeUnload,
  useBlocker,
  useLinkClickHandler,
  useLoaderData,
  useLocation,
  useMatch,
  useMatches,
  useNavigation,
  useNavigationType,
  useParams,
  useResolvedPath,
  useRouteError,
  useRouteLoaderData,
  useRoutes,
  useInRouterContext,
} from 'react-router'

export {
  /** @deprecated - Import useNavigate from @github-ui/react-router instead */
  useReactRouterNavigate as useDangerousNavigate,
  /** @deprecated - Import useSearchParams from @github-ui/react-router instead */
  useReactRouterSearchParams as useDangerousSearchParams,
}

export const PREVENT_AUTOFOCUS_KEY = '__gh__react-core-preventAutofocus'

export type BrowserHistory = ReturnType<typeof UNSAFE_createBrowserHistory>

// Note: the name for this interface went stale. It should really be something like
// NavigateOptionsWithExtensions.
export interface NavigateOptionsWithPreventAutofocus extends NavigateOptions {
  preventAutofocus?: boolean
  reloadDocument?: boolean
  preventTurbo?: boolean
}

export interface RoutesContextType {
  routes: RouteObject[]
}

export const RoutesContext = createContext<RoutesContextType>({routes: []})

/**
 * A wrapper around `react-router`'s useNavigate that adds support for:
 * - Soft navigation with Turbo for external links (when `reloadDocument` is not set)
 * - Preventing autofocus on navigation when `preventAutofocus` is set
 * - Starting a soft navigation with `@github-ui/soft-nav` for non-hash internal links to allow
 *   for features like the progress bar and transition animations
 */
export const useNavigate = (): ((to: To, options?: NavigateOptionsWithPreventAutofocus) => void) => {
  const {routes} = use(RoutesContext)
  const reactRouterNavigate = useReactRouterNavigate()
  return useCallback(
    (to, navigateOptions = {}) => {
      const pathname = resolvePath(to).pathname
      const isExternalToApp = !matchRoutes(routes, pathname)

      if (isExternalToApp || navigateOptions.reloadDocument) {
        const href = typeof to === 'string' ? to : createPath(to)
        if (navigateOptions.preventTurbo) {
          window.location.href = href
        } else {
          ;(async () => {
            const {softNavigate: turboSoftNavigate} = await import('@github-ui/soft-navigate')
            turboSoftNavigate(href)
          })()
        }
      } else {
        if (!isHashNavigation(location.href, to.toString())) {
          startSoftNav('react')
        }
        const {preventAutofocus, ...options} = navigateOptions
        reactRouterNavigate(
          to,
          preventAutofocus
            ? {
                ...options,
                state: {
                  [PREVENT_AUTOFOCUS_KEY]: true,
                  ...options.state,
                },
              }
            : options,
        )
      }
    },
    [reactRouterNavigate, routes],
  )
}

/**
 * An implementation of `useSearchParams` that mirrors `react-router`'s `useSearchParams` hook
 * but utilizes the wrapped `@github-ui/react-router` `useNavigate` to handle updates.
 */
export const useSearchParams = () => {
  const [searchParams] = useReactRouterSearchParams()
  const navigate = useNavigate()
  const {pathname} = useLocation()

  const setSearchParams = useCallback<
    (
      nextInit?: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
      navigateOpts?: NavigateOptionsWithPreventAutofocus,
    ) => void
  >(
    (nextInit, navigateOptions = {}) => {
      const newSearchParams = createSearchParams(typeof nextInit === 'function' ? nextInit(searchParams) : nextInit)
      navigate(
        {
          pathname,
          search: newSearchParams.toString(),
        },
        navigateOptions,
      )
    },
    [searchParams, navigate, pathname],
  )

  return [searchParams, setSearchParams] as const
}
